<template>
  <div>
    <PageHeaderLayout>
      <div class="main-page-content">
        <!-- 页面标题区域 -->
        <div class="page-title-section">
          <div class="title-content">
            <h1 class="page-title">
              <i class="el-icon-edit-outline"></i>
              订单跟进
            </h1>
            <p class="page-subtitle">订单号：{{ orderInfo.order_no || '加载中...' }}</p>
          </div>
        </div>

        <!-- 支付方式筛选：跳转订单列表 -->
        <div class="follow-pay-filter-bar">
          <span class="filter-label"><i class="el-icon-s-operation"></i> 支付方式筛选</span>
          <el-select
            v-model="filterPayMethod"
            placeholder="选择支付方式后打开列表"
            clearable
            filterable
            size="small"
            class="pay-method-select"
          >
            <el-option
              v-for="o in payMethodFilterOptions"
              :key="o.value"
              :label="o.label"
              :value="o.value"
            ></el-option>
          </el-select>
          <el-button type="primary" size="small" plain @click="goOrderListByPayMethod">
            打开订单列表
          </el-button>
        </div>

        <!-- 订单基本信息卡片 -->
        <div class="order-info-card">
          <div class="card-header">
            <i class="el-icon-s-order"></i>
            <span>订单基本信息</span>
          </div>
          <div class="card-content">
            <el-row :gutter="20">
              <el-col :span="8">
                <div class="info-item">
                  <span class="label">订单状态：</span>
                  <el-tag :type="getOrderStatusType(orderInfo.order_status)" size="medium">
                    {{ getOrderStatusText(orderInfo.order_status) }}
                  </el-tag>
                </div>
              </el-col>
              <el-col :span="8">
                <div class="info-item">
                  <span class="label">支付状态：</span>
                  <el-tag :type="getPayStatusType(orderInfo.pay_status)" size="medium">
                    {{ getPayStatusText(orderInfo.pay_status) }}
                  </el-tag>
                </div>
              </el-col>
              <el-col :span="8">
                <div class="info-item">
                  <span class="label">订单总额：</span>
                  <span class="value price">{{orderInfo.currency}}{{orderInfo.total_price}}</span>
                </div>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="12">
                <div class="info-item">
                  <span class="label">退款审批：</span>
                  <el-tag :type="getRefundApproveTagType(orderInfo.refund_approve_status)" size="medium">
                    {{ getRefundApproveText(orderInfo.refund_approve_status) }}
                  </el-tag>
                </div>
              </el-col>
              <el-col :span="12" v-if="refundPendingApproval">
                <el-button type="success" size="small" :loading="refundReviewLoading" @click="refundReview('approve')">审批通过</el-button>
                <el-button type="danger" size="small" plain :loading="refundReviewLoading" @click="refundReview('reject')">拒绝退款</el-button>
              </el-col>
            </el-row>
            <el-row :gutter="20" v-if="canOpenRefund || canManualRefund">
              <el-col :span="24">
                <el-button
                  v-if="canOpenRefund"
                  type="primary"
                  size="small"
                  :loading="refundOpenLoading"
                  @click="openRefundForCustomer"
                >
                  开放退款申请（客户可在客户端提交）
                </el-button>
                <el-button
                  v-if="canRevokeOpenRefund"
                  size="small"
                  :loading="refundOpenLoading"
                  @click="revokeOpenRefund"
                >
                  撤销开放
                </el-button>
                <el-button
                  v-if="canManualRefund"
                  type="warning"
                  size="small"
                  :loading="refundReviewLoading"
                  @click="refundReview('manual_refund')"
                >
                  手动确认退款（线下/在途退款已到账）
                </el-button>
                <span v-if="canManualRefund" class="status-tip" style="margin-left: 10px;">
                  银行转账：审批通过后请线下退款给客户，确认到账后点击上方按钮；支付宝：商家平台原路退回后确认；微信：退款处理中（PROCESSING）到账后确认。
                </span>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="8">
                <div class="info-item">
                  <span class="label">支付方式：</span>
                  <el-tag v-if="orderInfo.pay_method" :type="getPayMethodType(orderInfo.pay_method)" size="medium">
                    {{ getPayMethodText(orderInfo.pay_method) }}
                  </el-tag>
                  <span v-else class="value">—</span>
                </div>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="8">
                <div class="info-item">
                  <span class="label">已付金额：</span>
                  <span class="value paid-price">{{orderInfo.currency}}{{orderInfo.pay_amount}}</span>
                </div>
              </el-col>
              <el-col :span="8">
                <div class="info-item">
                  <span class="label">快递费：</span>
                  <span class="value logistic-price">{{orderInfo.currency}}{{orderInfo.logistic_price}}</span>
                </div>
              </el-col>
              <el-col :span="8">
                <div class="info-item">
                  <span class="label">折扣：</span>
                  <span class="value discount">{{orderInfo.discount}}%</span>
                </div>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="6">
                <div class="info-item">
                  <span class="label">快递公司：</span>
                  <span class="value">{{(orderInfo.Express && orderInfo.Express.express_name) || orderInfo.express_name || '暂无'}}</span>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="info-item">
                  <span class="label">到付账号：</span>
                  <span class="value">{{ orderInfo.express_account || '暂无' }}</span>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="info-item">
                  <span class="label">快递单号：</span>
                  <span class="value">{{orderInfo.express_no || '暂无'}}</span>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="info-item">
                  <span class="label">联系人：</span>
                  <span class="value">
                    {{(orderInfo.Address && orderInfo.Address.first_name) || (orderInfo.Customer && orderInfo.Customer.firstName) || '未知'}} 
                    {{(orderInfo.Address && orderInfo.Address.last_name) || (orderInfo.Customer && orderInfo.Customer.lastName) || ''}}
                  </span>
                </div>
              </el-col>
            </el-row>
            <el-row :gutter="20" v-if="orderInfo.pay_method === 'bank'">
              <el-col :span="24">
                <div class="info-item">
                  <span class="label">转账凭证：</span>
                  <el-link v-if="orderInfo.bank_voucher_url" type="primary" :href="orderInfo.bank_voucher_url" target="_blank">
                    查看已上传凭证
                  </el-link>
                  <span v-else class="value">未上传</span>
                  <el-tag v-if="orderInfo.pay_status === '1'" type="warning" size="mini" style="margin-left: 10px;">待人工确认到账</el-tag>
                  <el-tag v-if="orderInfo.pay_status === '2'" type="success" size="mini" style="margin-left: 10px;">已人工确认</el-tag>
                </div>
              </el-col>
            </el-row>
          </div>
        </div>

        <!-- 订单跟进表单卡片 -->
        <div class="follow-form-card">
          <div class="card-header">
            <i class="el-icon-edit"></i>
            <span>订单跟进信息</span>
          </div>
          <div class="card-content">
            <el-form 
              :model="followForm" 
              :rules="formRules" 
              ref="followForm" 
              label-width="120px"
              size="medium"
            >
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="订单状态" prop="order_status">
                    <el-select v-model="followForm.order_status" placeholder="请选择订单状态" style="width: 100%">
                      <el-option 
                        v-for="status in availableOrderStatuses" 
                        :key="status.value" 
                        :label="status.label" 
                        :value="status.value"
                        :disabled="status.disabled"
                      ></el-option>
                    </el-select>
                    <div class="status-tip" v-if="currentStatusInfo">
                      <i class="el-icon-info"></i>
                      当前状态：{{ currentStatusInfo.label }}。状态说明：0未处理、1用户确认、2商户确定、3生产、4质检、5发货、6签收成功、8删除订单、9取消订单；仅允许向前推进；5 后可设为 6；设为 8 需先 9；8 不出现在列表/详情查询
                    </div>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="快递费" prop="logistic_price">
                    <el-input-number 
                      v-model="followForm.logistic_price" 
                      :min="0" 
                      :precision="2" 
                      :step="0.01"
                      style="width: 100%"
                      placeholder="请输入快递费"
                    ></el-input-number>
                  </el-form-item>
                </el-col>
              </el-row>

              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="快递公司" prop="express_name">
                    <el-input 
                      v-model="followForm.express_name" 
                      placeholder="请输入快递公司名称"
                      :disabled="true"
                    >
                      <template slot="prepend">{{(orderInfo.Express && orderInfo.Express.express_name) || '未选择'}}</template>
                    </el-input>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="快递单号" prop="express_no">
                    <el-input 
                      v-model="followForm.express_no" 
                      placeholder="请输入快递单号"
                    ></el-input>
                  </el-form-item>
                </el-col>
              </el-row>

              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="折扣" prop="discount">
                    <el-input-number 
                      v-model="followForm.discount" 
                      :min="0" 
                      :max="100" 
                      :precision="2" 
                      :step="0.01"
                      style="width: 100%"
                      placeholder="请输入折扣百分比"
                    ></el-input-number>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="币种">
                    <el-tag type="info" size="medium">{{ orderInfo.currency || 'CNY' }}</el-tag>
                  </el-form-item>
                </el-col>
              </el-row>

              <el-form-item label="订单备注" prop="mark">
                <el-input 
                  v-model="followForm.mark" 
                  type="textarea" 
                  :rows="4"
                  placeholder="请输入订单备注信息"
                ></el-input>
              </el-form-item>
              <el-form-item label="转账凭证" v-if="orderInfo.pay_method === 'bank'">
                <ApeUploader
                  :is-image="true"
                  :limit="1"
                  :upload-file-list="uploadFileList"
                  @handleUploadSuccess="handleVoucherUploadSuccess"
                  @handleUploadRemove="handleVoucherUploadRemove"
                />
                <div class="status-tip" style="margin-top: 8px;">
                  <i class="el-icon-warning-outline"></i>
                  上传凭证后请在下方选择支付状态；确认已到账时请选「已支付」并填写已付金额
                </div>
              </el-form-item>

              <el-row :gutter="20" v-if="bankShowPayStatusFields">
                <el-col :span="12">
                  <el-form-item label="支付状态" prop="pay_status">
                    <el-select v-model="followForm.pay_status" placeholder="请选择支付状态" style="width: 100%">
                      <el-option label="未支付 / 待确认到账" value="1"></el-option>
                      <el-option label="已支付" value="2"></el-option>
                    </el-select>
                    <div class="status-tip" style="margin-top: 6px;">
                      <i class="el-icon-warning-outline"></i>
                      退款状态请走退款审批流（开放申请/审批），不可在此手改
                    </div>
                  </el-form-item>
                </el-col>
                <el-col :span="12" v-if="followForm.pay_status === '2'">
                  <el-form-item label="已付金额" prop="pay_amount">
                    <el-input-number
                      v-model="followForm.pay_amount"
                      :min="0"
                      :precision="2"
                      :step="0.01"
                      style="width: 100%"
                      placeholder="与银行到账金额一致"
                    ></el-input-number>
                  </el-form-item>
                </el-col>
              </el-row>

              <el-form-item>
                <el-button type="primary" @click="submitFollow" :loading="submitLoading">
                  <i class="el-icon-check"></i>
                  保存跟进信息
                </el-button>
                <el-button @click="resetForm">
                  <i class="el-icon-refresh"></i>
                  重置表单
                </el-button>
                <el-button @click="goBack">
                  <i class="el-icon-back"></i>
                  返回列表
                </el-button>
              </el-form-item>
            </el-form>
          </div>
        </div>

        <!-- 状态流转图卡片 -->
        <div class="status-flow-card">
          <div class="card-header">
            <i class="el-icon-s-operation"></i>
            <span>订单状态流转图</span>
          </div>
          <div class="card-content">
            <div class="status-flow-diagram">
              <div class="flow-line">
                <div class="status-node" 
                  v-for="status in orderStatusFlowDisplayOrder" 
                  :key="status.value"
                  :class="{ 
                    'current': status.value === orderInfo.order_status,
                    'completed': isStatusCompleted(status.value),
                    'available': isStatusAvailable(status.value)
                  }"
                >
                  <div class="status-icon">
                    <i :class="getStatusIcon(status.value)"></i>
                  </div>
                  <div class="status-label">{{ status.label }}</div>
                  <div class="status-value">{{ status.value }}</div>
                </div>
              </div>
              <div class="flow-legend">
                <div class="legend-item">
                  <div class="legend-color current"></div>
                  <span>当前状态</span>
                </div>
                <div class="legend-item">
                  <div class="legend-color completed"></div>
                  <span>已完成</span>
                </div>
                <div class="legend-item">
                  <div class="legend-color available"></div>
                  <span>可跳转</span>
                </div>
                <div class="legend-item">
                  <div class="legend-color disabled"></div>
                  <span>不可用</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 操作记录卡片 -->
        <div class="operation-record-card">
          <div class="card-header">
            <i class="el-icon-time"></i>
            <span>操作记录</span>
            <el-button 
              size="mini" 
              type="info" 
              icon="el-icon-view" 
              style="margin-left: auto;"
              @click="debugAllRecords"
            >
              调试数据
            </el-button>
          </div>
          <div class="card-content">
            <el-timeline>
              <el-timeline-item
                v-for="(record, index) in operationRecords"
                :key="index"
                :timestamp="formatDateTime(record.operated_at)"
                :type="getTimelineItemType(record.record_type)"
              >
                <div class="record-content">
                  <div class="record-mark">{{ record.mark }}</div>
                  <div class="record-user">操作人：{{ getOperatorInfo(record) }}</div>
                  <!-- 显示解析后的变更详情 -->
                  <div v-if="record.change && getParsedChanges(record)" class="record-changes">
                    <div class="changes-header">
                      <i class="el-icon-document"></i>
                      <span>变更详情：</span>
                    </div>
                    <div class="changes-content">
                      <div v-for="(change, fieldName) in getParsedChanges(record)" :key="fieldName" class="change-item">
                        <div class="change-field">
                          <el-tag size="mini" type="info">{{ getFieldLabel(fieldName) }}</el-tag>
                        </div>
                        <div class="change-values">
                          <span class="change-from">{{ formatChangeValue(change.from, fieldName) }}</span>
                          <i class="el-icon-arrow-right"></i>
                          <span class="change-to">{{ formatChangeValue(change.to, fieldName) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- 显示变更描述 -->
                  <div v-if="record.change && getChangeDescriptions(record).length > 0" class="change-descriptions">
                    <div class="descriptions-header">
                      <i class="el-icon-info"></i>
                      <span>变更说明：</span>
                    </div>
                    <div class="descriptions-content">
                      <el-tag 
                        v-for="(desc, descIndex) in getChangeDescriptions(record)" 
                        :key="descIndex"
                        size="mini" 
                        type="success"
                        class="description-tag"
                      >
                        {{ desc }}
                      </el-tag>
                    </div>
                  </div>
                </div>
              </el-timeline-item>
            </el-timeline>
          </div>
        </div>
      </div>
    </PageHeaderLayout>
  </div>
</template>

<script>
import PageHeaderLayout from '@/layouts/PageHeaderLayout'
import ApeUploader from '@/components/ApeUploader'
import { PAY_METHOD_OPTIONS } from '@/constants/payMethods'
import { mapGetters } from 'vuex'

export default {
  name: 'CustomerOrderFollow',
  components: {
    PageHeaderLayout,
    ApeUploader
  },
  data() {
    return {
      orderId: '',
      orderInfo: {},
      followForm: {
        order_status: '',
        logistic_price: 0,
        express_name: '',
        express_no: '',
        discount: 0,
        mark: '',
        bank_voucher_url: '',
        pay_status: '',
        pay_amount: 0
      },
      submitLoading: false,
      operationRecords: [],
      uploadFileList: [],
      filterPayMethod: '',
      refundReviewLoading: false,
      refundOpenLoading: false
    }
  },
  computed: {
    ...mapGetters(['userPermissions']),
    payMethodFilterOptions() {
      return PAY_METHOD_OPTIONS
    },

    bankShowPayStatusFields() {
      const pm = this.normalizePayMethod(this.orderInfo.pay_method)
      return pm === 'bank' && !!(this.orderInfo.bank_voucher_url || this.followForm.bank_voucher_url)
    },

    refundPendingApproval() {
      // 三态：2 = 客户已申请，待商户审核
      return this.orderInfo.refund_approve_status === '2'
    },
    /** 已支付、用户确认态、尚未进入退款流程 → 商户可开放客户申请 */
    canOpenRefund() {
      const s = String(this.orderInfo.refund_approve_status || '').trim()
      return this.orderInfo.pay_status === '2' &&
        this.orderInfo.order_status === '1' &&
        (s === '' || s === null)
    },
    canRevokeOpenRefund() {
      return this.orderInfo.refund_approve_status === '1'
    },
    canManualRefund() {
      const pm = this.normalizePayMethod(this.orderInfo.pay_method)
      const approved = this.orderInfo.refund_approve_status === '3'
      // 评审 B4：放开到 alipay/wechat——支付宝无退款 API 走线下原路退，
      // 微信 PROCESSING（退款在途）也靠 manual_refund 收口，原来只有 bank
      // 能点，其他线下渠道订单的退款确认按钮永远不出现，单据卡死。
      const offlineChannels = ['bank', 'alipay', 'wechat']
      return offlineChannels.includes(pm) && approved && this.orderInfo.pay_status !== '4'
    },

    formRules() {
      const rules = {
        order_status: [
          { required: true, message: '请选择订单状态', trigger: 'change' }
        ],
        logistic_price: [
          { required: true, message: '请输入快递费', trigger: 'blur' }
        ]
      }
      if (this.bankShowPayStatusFields) {
        rules.pay_status = [
          { required: true, message: '已有转账凭证，请选择支付状态', trigger: 'change' }
        ]
      }
      if (this.followForm.pay_status === '2') {
        rules.pay_amount = [
          { required: true, message: '确认已支付时请填写已付金额', trigger: 'blur' }
        ]
      }
      return rules
    },
    
    // 所有订单状态定义
    orderStatusList() {
      return [
        { value: '0', label: '未处理', type: 'info' },
        { value: '1', label: '用户确认', type: 'success' },
        { value: '2', label: '商户确定', type: 'warning' },
        { value: '3', label: '生产', type: 'info' },
        { value: '4', label: '质检', type: 'info' },
        { value: '5', label: '发货', type: 'success' },
        { value: '6', label: '签收成功', type: 'success' },
        { value: '8', label: '删除订单', type: 'danger' },
        { value: '9', label: '取消订单', type: 'info' }
      ]
    },

    // 流转图：0→6 主流程，其后 9 取消、8 删除（与后端 order_status 枚举一致，不含 7）
    orderStatusFlowDisplayOrder() {
      const list = this.orderStatusList
      const keys = ['0', '1', '2', '3', '4', '5', '6', '9', '8']
      return keys.map((v) => list.find((s) => s.value === v)).filter(Boolean)
    },
    
    // 当前状态信息
    currentStatusInfo() {
      if (!this.orderInfo.order_status) return null
      return this.orderStatusList.find(status => status.value === this.orderInfo.order_status) || null
    },
    
    // 可用的订单状态：向前推进；8「删除」仅能从 9「取消」进入；已删除(8)不可再改
    availableOrderStatuses() {
      if (!this.orderInfo.order_status) return this.orderStatusList

      const cur = this.orderInfo.order_status
      if (cur === '8') {
        return this.orderStatusList.map(status => ({ ...status, disabled: true }))
      }

      const currentIndex = this.orderStatusList.findIndex(status => status.value === cur)
      if (currentIndex === -1) return this.orderStatusList

      return this.orderStatusList.map((status, index) => {
        let disabled = false
        if (index < currentIndex) {
          disabled = true
        }
        if (status.value === '8' && cur !== '9') {
          disabled = true
        }
        return {
          ...status,
          disabled
        }
      })
    }
  },
  methods: {
    // 获取订单详情
    async getOrderDetail() {
      try {
        const res = await this.$api.post('/service/customer_order/detail', {
          id: this.orderId
        })
        
        // 检查组件是否已销毁
        if (this._isDestroyed) return
        
        console.log('API响应:', res)
        if (this.$apiSuccess(res)) {
          // 根据实际API响应，数据在res.data.detail中
          this.orderInfo = res.data.detail || res.data          
          // 初始化表单数据
          this.followForm = {
            order_status: this.orderInfo.order_status || '',
            logistic_price: this.orderInfo.logistic_price || 0,
            express_name: (this.orderInfo.Express && this.orderInfo.Express.express_name) || this.orderInfo.express_name || '',
            express_no: this.orderInfo.express_no || '',
            discount: this.orderInfo.discount || 0,
            mark: this.orderInfo.mark || '',
            bank_voucher_url: this.orderInfo.bank_voucher_url || '',
            pay_status: this.orderInfo.pay_status || '',
            pay_amount: this.orderInfo.pay_amount != null ? this.orderInfo.pay_amount : 0
          }
          this.uploadFileList = this.orderInfo.bank_voucher_url ? [{ name: 'bank-voucher', url: this.orderInfo.bank_voucher_url, path: this.orderInfo.bank_voucher_url }] : []
          const pm = this.orderInfo.pay_method
          this.filterPayMethod = pm ? this.normalizePayMethod(pm) : ''
        } else {
          this.$message.error(res.message || '获取订单详情失败')
        }
      } catch (error) {
        // 检查组件是否已销毁
        if (this._isDestroyed) return
        
        console.error('获取订单详情失败:', error)
        this.$message.error('获取订单详情失败: ' + error.message)
      }
    },

    // 获取操作记录
    async getOperationRecords() {
      try {
        const condition = {
          query: 'order_no=?',
          params: [this.orderInfo.order_no]
        }
        const res = await this.$api.post('/service/ola_app_user_order_follow_record/list', condition)
        console.log('操作记录:', res)
        if (this.$apiSuccess(res)) {
          // 根据实际API响应，数据在res.data.list中
          this.operationRecords = res.data.list || []
          console.log('解析后的操作记录:', this.operationRecords)
        }
      } catch (error) {
        console.error('获取操作记录失败:', error)
        this.operationRecords = []
      }
    },

    // 提交跟进信息
    async submitFollow() {
      try {
        await this.$refs.followForm.validate()

        this.submitLoading = true
        
        const body = {
          id: this.orderId,
          order_status: this.followForm.order_status,
          logistic_price: this.followForm.logistic_price,
          express_name: this.followForm.express_name,
          express_no: this.followForm.express_no,
          discount: this.followForm.discount,
          mark: this.followForm.mark,
          bank_voucher_url: this.followForm.bank_voucher_url
        }
        if (this.normalizePayMethod(this.orderInfo.pay_method) === 'bank') {
          body.pay_status = this.followForm.pay_status
          if (this.followForm.pay_status === '2') {
            body.pay_amount = this.followForm.pay_amount
          }
        }
        const res = await this.$api.post('/service/customer_order/update_follow', body)
        
        // 检查组件是否已销毁
        if (this._isDestroyed) return
        
        if (this.$apiSuccess(res)) {
          this.$message.success('订单跟进信息保存成功')
          // 重新获取订单详情
          await this.getOrderDetail()
          // 重新获取操作记录
          await this.getOperationRecords()
        } else {
          this.$message.error(res.message || '保存失败')
        }
      } catch (error) {
        // 检查组件是否已销毁
        if (this._isDestroyed) return
        
        console.error('提交跟进信息失败:', error)
        this.$message.error('提交失败，请检查表单数据')
      } finally {
        // 检查组件是否已销毁
        if (!this._isDestroyed) {
          this.submitLoading = false
        }
      }
    },

    // 重置表单
    resetForm() {
      this.$refs.followForm.resetFields()
      // 重新初始化表单数据
      this.followForm = {
        order_status: this.orderInfo.order_status || '',
        logistic_price: this.orderInfo.logistic_price || 0,
        express_name: (this.orderInfo.Express && this.orderInfo.Express.express_name) || this.orderInfo.express_name || '',
        express_no: this.orderInfo.express_no || '',
        discount: this.orderInfo.discount || 0,
        mark: this.orderInfo.mark || '',
        bank_voucher_url: this.orderInfo.bank_voucher_url || '',
        pay_status: this.orderInfo.pay_status || '',
        pay_amount: this.orderInfo.pay_amount != null ? this.orderInfo.pay_amount : 0
      }
      this.uploadFileList = this.orderInfo.bank_voucher_url ? [{ name: 'bank-voucher', url: this.orderInfo.bank_voucher_url, path: this.orderInfo.bank_voucher_url }] : []
    },
    handleVoucherUploadSuccess(file, fileList) {
      this.followForm.bank_voucher_url = file
      this.uploadFileList = fileList
    },
    handleVoucherUploadRemove(file, fileList) {
      this.followForm.bank_voucher_url = ''
      this.uploadFileList = fileList
    },
    normalizePayMethod(method) {
      const normalized = String(method || '').trim().toLowerCase()
      if (normalized === 'bank_transfer' || normalized === 'banktransfer') return 'bank'
      return normalized
    },
    getPayMethodType(method) {
      if (!method) return 'info'
      const normalized = this.normalizePayMethod(method)
      const methodTypeMap = {
        paypal: 'success',
        alipay: 'primary',
        wechat: 'warning',
        visa: 'danger',
        bank: 'info'
      }
      return methodTypeMap[normalized] || 'info'
    },
    getPayMethodText(method) {
      if (!method) return '—'
      const normalized = this.normalizePayMethod(method)
      const methodTextMap = {
        paypal: 'PayPal',
        alipay: '支付宝',
        wechat: '微信',
        visa: '信用卡（Visa）',
        bank: '银行转账'
      }
      return methodTextMap[normalized] || '未知支付方式'
    },
    goOrderListByPayMethod() {
      const q = {}
      if (this.filterPayMethod) q.pay_method = this.filterPayMethod
      this.$router.push({ path: '/customer/order', query: q })
    },

    // 返回列表
    goBack() {
      // 使用更安全的路由跳转方式，避免权限策略违规
      if (this.$router.currentRoute.name) {
        this.$router.push('/customer/order')
      } else {
        this.$router.go(-1)
      }
    },

    getRefundApproveText(code) {
      const c = String(code || '').trim()
      const m = {
        '': '未开放',
        '1': '已开放申请（待客户提交）',
        '2': '客户已申请，待审核',
        '3': '审批通过'
      }
      return m[c] != null ? m[c] : (c ? '历史状态(' + c + ')' : '—')
    },
    getRefundApproveTagType(code) {
      const c = String(code || '').trim()
      if (c === '3') return 'success'
      if (c === '2') return 'warning'
      if (c === '1') return 'primary'
      return 'info'
    },
    async refundReview(action) {
      this.refundReviewLoading = true
      try {
        const res = await this.$api.post('/service/customer_order/refund_approve_review', {
          id: this.orderId,
          action
        })
        if (this.$apiSuccess(res)) {
          const msgMap = {
            approve: '审批已通过',
            manual_refund: '已手动确认退款成功',
            reject: '已拒绝退款，客户将收到通知邮件'
          }
          this.$message.success(msgMap[action] || '操作成功')
          await this.getOrderDetail()
          await this.getOperationRecords()
        } else {
          this.$message.error(res.message || '操作失败')
        }
      } catch (e) {
        this.$message.error((e && e.message) || '请求失败')
      } finally {
        this.refundReviewLoading = false
      }
    },
    async openRefundForCustomer() {
      this.refundOpenLoading = true
      try {
        const res = await this.$api.post('/service/customer_order/update_follow', {
          id: this.orderId,
          refund_approve_status: '1'
        })
        if (this.$apiSuccess(res)) {
          this.$message.success('已开放退款申请，客户可在订单列表/详情中发起申请')
          await this.getOrderDetail()
          await this.getOperationRecords()
        } else {
          this.$message.error(res.message || '操作失败')
        }
      } catch (e) {
        this.$message.error((e && e.message) || '操作失败')
      } finally {
        this.refundOpenLoading = false
      }
    },
    async revokeOpenRefund() {
      this.refundOpenLoading = true
      try {
        const res = await this.$api.post('/service/customer_order/update_follow', {
          id: this.orderId,
          refund_approve_status: ''
        })
        if (this.$apiSuccess(res)) {
          this.$message.success('已撤销开放')
          await this.getOrderDetail()
          await this.getOperationRecords()
        } else {
          this.$message.error(res.message || '操作失败')
        }
      } catch (e) {
        this.$message.error((e && e.message) || '操作失败')
      } finally {
        this.refundOpenLoading = false
      }
    },

    // 获取订单状态类型
    getOrderStatusType(status) {
      const statusInfo = this.orderStatusList.find(s => s.value === status)
      if (statusInfo) return statusInfo.type
      return 'info'
    },

    // 获取订单状态文本
    getOrderStatusText(status) {
      const statusInfo = this.orderStatusList.find(s => s.value === status)
      if (statusInfo) return statusInfo.label
      return '未知状态'
    },

    // 获取支付状态类型
    getPayStatusType(status) {
      const statusTypeMap = {
        '1': 'info',
        '2': 'success',
        '3': 'warning',
        '4': 'success',
        '5': 'danger'
      }
      return statusTypeMap[status] || 'info'
    },

    // 获取支付状态文本
    getPayStatusText(status) {
      const statusTextMap = {
        '1': '未支付',
        '2': '已支付',
        '3': '申请退款',
        '4': '退款成功',
        '5': '拒绝退款'
      }
      return statusTextMap[status] || '未知状态'
    },

    // 获取时间线项目类型
    getTimelineItemType(recordType) {
      return recordType === '1' ? 'primary' : 'success'
    },
    
    // 获取状态图标
    getStatusIcon(statusValue) {
      const iconMap = {
        '0': 'el-icon-time',
        '1': 'el-icon-check',
        '2': 'el-icon-s-check',
        '3': 'el-icon-s-operation',
        '4': 'el-icon-s-opportunity',
        '5': 'el-icon-truck',
        '6': 'el-icon-circle-check',
        '8': 'el-icon-delete',
        '9': 'el-icon-circle-close'
      }
      return iconMap[statusValue] || 'el-icon-question'
    },
    
    // 判断状态是否已完成
    isStatusCompleted(statusValue) {
      if (!this.orderInfo.order_status) return false
      
      const currentIndex = this.orderStatusList.findIndex(status => status.value === this.orderInfo.order_status)
      const statusIndex = this.orderStatusList.findIndex(status => status.value === statusValue)
      
      if (currentIndex === -1 || statusIndex === -1) return false
      
      if (statusValue === '6' || statusValue === '8' || statusValue === '9') return false
      
      return statusIndex < currentIndex
    },
    
    // 判断状态是否可用（可跳转）
    isStatusAvailable(statusValue) {
      if (!this.orderInfo.order_status) return true
      
      const availableStatus = this.availableOrderStatuses.find(status => status.value === statusValue)
      return availableStatus && !availableStatus.disabled
    },

    // 格式化日期时间
    formatDateTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      if (isNaN(date.getTime())) return ''
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },

    // 获取操作员信息
    getOperatorInfo(record) {
      if (record.change) {
        try {
          const changeData = JSON.parse(record.change)
          return changeData.operator_email || changeData.operator_id || '系统'
        } catch (e) {
          return record.user_id || '系统'
        }
      }
      return record.user_id || '系统'
    },

    // 解析变更数据
    getParsedChanges(record) {
      if (!record.change) return {}
      
      try {
        const changeData = JSON.parse(record.change)
        console.log('解析的变更数据:', changeData)
        return changeData.changed_fields || {}
      } catch (e) {
        console.error('解析变更数据失败:', e, '原始数据:', record.change)
        return {}
      }
    },

    // 获取变更描述
    getChangeDescriptions(record) {
      if (!record.change) return []
      
      try {
        const changeData = JSON.parse(record.change)
        console.log('解析的变更描述:', changeData.change_descriptions)
        return changeData.change_descriptions || []
      } catch (e) {
        console.error('解析变更描述失败:', e, '原始数据:', record.change)
        return []
      }
    },

    // 获取字段标签
    getFieldLabel(fieldName) {
      const fieldLabels = {
        'order_status': '订单状态',
        'pay_status': '支付状态',
        'pay_amount': '已付金额',
        'logistic_price': '快递费',
        'express_name': '快递公司',
        'express_no': '快递单号',
        'discount': '折扣',
        'mark': '备注',
        'bank_voucher_url': '转账凭证'
      }
      return fieldLabels[fieldName] || fieldName
    },

    // 格式化变更值
    formatChangeValue(value, fieldName = '') {
      if (value === null || value === undefined || value === '') {
        return '无'
      }
      if (typeof value === 'number') {
        return value.toFixed(2)
      }
      
      if (fieldName === 'order_status' && this.isOrderStatusField(value)) {
        return this.getOrderStatusText(value)
      }
      if (fieldName === 'pay_status') {
        return this.getPayStatusText(value)
      }

      return String(value)
    },

    // 判断是否为订单状态字段
    isOrderStatusField(value) {
      // 检查是否为订单状态数字
      const statusNumbers = ['0', '1', '2', '3', '4', '5', '6', '8', '9']
      return statusNumbers.includes(String(value))
    },

    // 调试方法：检查记录数据
    debugRecord(record) {
      console.log('调试记录:', record)
      if (record.change) {
        console.log('变更JSON:', record.change)
        try {
          const parsed = JSON.parse(record.change)
          console.log('解析结果:', parsed)
        } catch (e) {
          console.error('JSON解析失败:', e)
        }
      }
    },

    // 调试所有记录
    debugAllRecords() {
      console.log('=== 调试所有操作记录 ===')
      console.log('操作记录总数:', this.operationRecords.length)
      this.operationRecords.forEach((record, index) => {
        console.log(`记录 ${index + 1}:`, record)
        this.debugRecord(record)
      })
      console.log('=== 调试完成 ===')
    }
  },
  async mounted() {
    this.orderId = this.$route.params.id
    if (this.orderId) {
      try {
        await this.getOrderDetail()
        await this.getOperationRecords()
      } catch (error) {
        // 检查组件是否已销毁
        if (this._isDestroyed) return
        
        console.error('页面初始化失败:', error)
        this.$message.error('页面初始化失败')
        this.goBack()
      }
    } else {
      this.$message.error('订单ID不能为空')
      this.goBack()
    }
  },
  beforeDestroy() {
    // 清理异步操作，防止页面卸载时的权限策略违规
    this.submitLoading = false
    // 取消所有可能的异步操作
    if (this.$refs.followForm) {
      this.$refs.followForm.clearValidate()
    }
  }
}
</script>

<style lang="stylus" scoped>
.follow-pay-filter-bar
  display flex
  align-items center
  flex-wrap wrap
  gap 10px
  background white
  border-radius 12px
  padding 14px 18px
  margin-bottom 20px
  box-shadow 0 2px 12px rgba(0, 0, 0, 0.06)
  .filter-label
    font-size 14px
    font-weight 500
    color #303133
    margin-right 4px
    i
      margin-right 6px
      color #409eff
  .pay-method-select
    width 220px

// 页面标题区域样式
.page-title-section
  background linear-gradient(135deg, #667eea 0%, #764ba2 100%)
  border-radius 12px
  padding 24px
  margin-bottom 20px
  color white
  
  .title-content
    .page-title
      margin 0 0 8px 0
      font-size 24px
      font-weight 600
      display flex
      align-items center
      
      i
        margin-right 12px
        font-size 28px
    
    .page-subtitle
      margin 0
      opacity 0.9
      font-size 14px

// 卡片通用样式
.order-info-card,
.follow-form-card,
.operation-record-card
  background white
  border-radius 12px
  margin-bottom 20px
  box-shadow 0 2px 12px rgba(0, 0, 0, 0.08)
  
  .card-header
    padding 16px 20px
    border-bottom 1px solid #f0f0f0
    display flex
    align-items center
    font-weight 600
    color #303133
    
    i
      margin-right 8px
      color #409eff
    
    span
      flex 1
  
  .card-content
    padding 20px

// 订单信息样式
.order-info-card
  .info-item
    display flex
    align-items center
    margin-bottom 12px
    
    .label
      color #909399
      margin-right 8px
      min-width 80px
      font-weight 500
    
    .value
      color #303133
      font-weight 500
      
      &.price
        color #f56c6c
        font-weight 600
        
      &.paid-price
        color #67c23a
        font-weight 600
        
      &.logistic-price
        color #e6a23c
        font-weight 600
        
      &.discount
        color #409eff
        font-weight 600

// 表单样式
.follow-form-card
  /deep/ .el-form-item
    margin-bottom 20px
    
  /deep/ .el-input-number
    .el-input__inner
      text-align left
  
  .status-tip
    margin-top 8px
    padding 8px 12px
    background #f0f9ff
    border 1px solid #b3d8ff
    border-radius 6px
    color #409eff
    font-size 12px
    display flex
    align-items center
    
    i
      margin-right 6px
      font-size 14px

// 状态流转图样式
.status-flow-card
  .status-flow-diagram
    .flow-line
      display flex
      flex-wrap wrap
      gap 12px
      margin-bottom 20px
      
      .status-node
        display flex
        flex-direction column
        align-items center
        padding 12px
        border-radius 8px
        border 2px solid #e4e7ed
        background white
        min-width 80px
        transition all 0.3s ease
        
        &.current
          border-color #409eff
          background #f0f9ff
          box-shadow 0 0 8px rgba(64, 158, 255, 0.3)
          
        &.completed
          border-color #67c23a
          background #f0f9ff
          
        &.available
          border-color #e6a23c
          background #fdf6ec
          
        .status-icon
          font-size 20px
          margin-bottom 4px
          color #409eff
          
        .status-label
          font-size 12px
          font-weight 500
          text-align center
          margin-bottom 2px
          
        .status-value
          font-size 10px
          color #909399
          background #f5f7fa
          padding 2px 6px
          border-radius 4px
    
    .flow-legend
      display flex
      flex-wrap wrap
      gap 16px
      
      .legend-item
        display flex
        align-items center
        font-size 12px
        
        .legend-color
          width 16px
          height 16px
          border-radius 4px
          margin-right 6px
          border 2px solid #e4e7ed
          
          &.current
            background #409eff
            border-color #409eff
            
          &.completed
            background #67c23a
            border-color #67c23a
            
          &.available
            background #e6a23c
            border-color #e6a23c
            
          &.disabled
            background #f5f7fa
            border-color #e4e7ed

// 操作记录样式
.operation-record-card
  .record-content
    .record-mark
      color #303133
      font-weight 500
      margin-bottom 4px
    
    .record-user
      color #909399
      font-size 12px
      margin-bottom 12px
    
    .record-changes
      margin-top 12px
      padding 12px
      background #f8f9fa
      border-radius 6px
      border-left 3px solid #409eff
      
      .changes-header
        display flex
        align-items center
        margin-bottom 8px
        font-weight 500
        color #409eff
        
        i
          margin-right 6px
          font-size 14px
        
        span
          font-size 13px
      
      .changes-content
        .change-item
          display flex
          align-items center
          margin-bottom 6px
          
          .change-field
            margin-right 12px
            min-width 80px
          
          .change-values
            display flex
            align-items center
            flex 1
            
            .change-from
              color #909399
              text-decoration line-through
              margin-right 8px
              font-size 12px
            
            .el-icon-arrow-right
              color #409eff
              margin 0 8px
              font-size 12px
            
            .change-to
              color #67c23a
              font-weight 500
              font-size 12px
    
    .change-descriptions
      margin-top 12px
      
      .descriptions-header
        display flex
        align-items center
        margin-bottom 8px
        font-weight 500
        color #67c23a
        
        i
          margin-right 6px
          font-size 14px
        
        span
          font-size 13px
      
      .descriptions-content
        display flex
        flex-wrap wrap
        gap 6px
        
        .description-tag
          margin-right 6px
          margin-bottom 4px

// 响应式设计
@media (max-width: 768px)
  .page-title-section
    padding 16px
    
    .page-title
      font-size 20px
  
  .order-info-card,
  .follow-form-card,
  .operation-record-card
    .card-content
      padding 16px

// 全局样式优化
/deep/ .el-button
  border-radius 6px
  transition all 0.3s ease
  
  &:hover
    transform translateY(-1px)
    box-shadow 0 4px 12px rgba(64, 158, 255, 0.3)

/deep/ .el-input
  .el-input__inner
    border-radius 6px
    transition all 0.3s ease
    
    &:focus
      box-shadow 0 0 8px rgba(64, 158, 255, 0.2)

/deep/ .el-select
  .el-input__inner
    border-radius 6px

/deep/ .el-tag
  border-radius 6px
  font-weight 500

/deep/ .el-timeline-item__node
  border-radius 50%

/deep/ .el-timeline-item__wrapper
  padding-left 20px
</style> 